/*──────────────────────────────────────────────────────────────
 *  File: src/components/ExplorerModal.jsx   — fully regenerated 2025-10-23
 *
 *  Lease File Explorer  ▸  Upload  ▸  Delete  ▸  Preview
 *  • Directory root:   <subdomain>/Leases/…
 *  • Upload  : drag-drop, “Upload” button    (uses FileManager helpers)
 *  • Delete  : trash icon (file) or “Delete folder” (folder)
 *              – folders deleted recursively (server API, chunked concurrency)
 *  • View    : list ⇆ grid, remembers last choice in localStorage
 *  • Layout  : header fixed, body scrolls, modal ≤ 75vh
 *  • Auth    : auto-forwards ?token=… and company=subdomain in all API calls
 *──────────────────────────────────────────────────────────────*/

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  listFiles,
  uploadFile,
  signedUrl,
  companySlug,
} from "../utils/FileManager";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faChevronLeft,
  faChevronRight,
  faFileExcel,
  faFilePdf,
  faFileWord,
  faFolder,
  faFolderOpen,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
  faPlus,
  faSearch,
  faSortAmountDown,
  faTh,
  faTimes,
  faTrash,
  faUndo,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";

/*────────────────── API endpoint (matches utils/FileManager) ─*/
const API_ENDPOINT = "https://house.ogendeavors.com/api/files.php";

/*────────────────── helpers ─────────────────────────────────*/
const LS = { VIEW: "ogFeView" };

const isImg = (k) => /\.(jpe?g|png|gif|webp|bmp|avif|tiff?)$/i.test(k);
const isPdf = (k) => /\.pdf$/i.test(k);
const isXls = (k) => /\.(xlsx?|xlsm?)$/i.test(k);
const isDoc = (k) => /\.(docx?|rtf)$/i.test(k);

const iconOf = (kind) =>
  kind === "pdf"
    ? faFilePdf
    : kind === "xls"
    ? faFileExcel
    : kind === "doc"
    ? faFileWord
    : faFilePdf;

const officeUrl = (raw) =>
  `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
    raw
  )}`;

const fmtSz = (b) =>
  b >= 1 << 20 ? `${(b / (1 << 20)).toFixed(1)} MB` : `${(b / 1024) | 0} KB`;

/**
 * Local API poster that mirrors utils/FileManager's auth behavior:
 * - forwards ?token
 * - adds company=subdomain
 */
async function apiPost(payload) {
  let merged = payload || {};
  try {
    if (typeof window !== "undefined") {
      const sp = new URLSearchParams(window.location.search);
      const tkn = sp.get("token");
      if (tkn && !merged.token) merged = { ...merged, token: tkn };
      if (!merged.company) merged = { ...merged, company: companySlug() };
    }
  } catch {}
  const res = await fetch(API_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(merged),
  });
  if (!res.ok) {
    let txt = "";
    try {
      txt = await res.text();
    } catch {}
    throw new Error(`API ${merged?.action || ""} failed: ${res.status} ${txt}`);
  }
  return res.json();
}

/*────────────────── modal shell ─────────────────────────────*/
const Modal = ({ open, onClose, theme, children }) =>
  !open ? null : (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        className={`relative w-full max-w-5xl mx-4 md:mx-0 rounded-lg shadow-2xl ${
          theme === "dark" ? "bg-gray-800" : "bg-white"
        }`}
        style={{ maxHeight: "75vh" }}
      >
        {children}
      </div>
    </div>
  );

/*────────────────── component ───────────────────────────────*/
export default function ExplorerModal({
  open,
  onClose,
  theme = "light",
  // companyName is optional and used only for a cosmetic label; we prefer companySlug()
  companyName,
}) {
  /*──── state ──────────────────────────────────────────────*/
  const [prefix, setPrefix] = useState(""); // after Leases/
  const [data, setData] = useState({ dirs: [], files: [] });
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(localStorage.getItem(LS.VIEW) || "list");
  const [sortBy, setSortBy] = useState("name");
  const [asc, setAsc] = useState(true);
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(null);
  const [zoom, setZoom] = useState(1);
  const fileRef = useRef(null);

  const ROOT = "Leases/";

  /*──── load dir ───────────────────────────────────────────*/
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const slug = companySlug();
      const plain = ROOT + prefix; // "Leases/…"
      const base = `${slug}/${plain}`; // "<subdomain>/Leases/…"
      const raw = await listFiles(plain);

      const dirs = new Set();
      const files = [];

      raw.forEach((o) => {
        if (!o.Key) return;
        // Normalize to relative name under current folder
        const rel = o.Key.startsWith(base) ? o.Key.slice(base.length) : o.Key;

        // Folders show up if the rel contains a slash—even if API returns "folder" rows
        if (o.type === "folder" || rel.includes("/")) {
          const seg = rel.split("/")[0];
          if (seg) dirs.add(seg);
          return;
        }

        const kind = isPdf(o.Key)
          ? "pdf"
          : isXls(o.Key)
          ? "xls"
          : isDoc(o.Key)
          ? "doc"
          : isImg(o.Key)
          ? "img"
          : null;

        // Keep only previewable/known kinds; others can still open via a link
        files.push({
          key: o.Key,
          rel,
          date: o.LastModified ? new Date(o.LastModified) : null,
          size: o.Size,
          kind,
        });
      });

      // Get presigned GETs
      const urls = await Promise.all(files.map((f) => signedUrl(f.key)));
      files.forEach((f, i) => (f.url = urls[i]));

      setData({ dirs: [...dirs].sort(), files });
    } catch (e) {
      console.error("Explorer load:", e);
    } finally {
      setBusy(false);
    }
  }, [prefix]);

  useEffect(() => {
    if (open) load();
  }, [open, prefix, load]);

  /*──── filtered / sorted lists ────────────────────────────*/
  const { dirHits, fileHits } = useMemo(() => {
    const needle = q.trim().toLowerCase();

    const dirs = needle
      ? data.dirs.filter((d) => d.toLowerCase().includes(needle))
      : data.dirs;

    let files = needle
      ? data.files.filter((f) => f.rel.toLowerCase().includes(needle))
      : data.files;

    files = [...files].sort((a, b) => {
      const d = asc ? 1 : -1;
      if (sortBy === "name") return a.rel.localeCompare(b.rel) * d;
      if (sortBy === "date") return ((a.date || 0) - (b.date || 0)) * d;
      if (sortBy === "size") return (a.size - b.size) * d;
      return 0;
    });

    return { dirHits: dirs, fileHits: files };
  }, [data, q, sortBy, asc]);

  /*──── refresh dir ────────────────────────────────────────*/
  const refresh = () => load();

  /*──── upload ─────────────────────────────────────────────*/
  const doUpload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const folder = prefix.replace(/\/$/, "");
      for (const f of files) await uploadFile(f, folder);
      refresh();
    } catch (e) {
      console.error("Upload failed:", e);
      alert("Upload failed (see console).");
    } finally {
      setBusy(false);
    }
  };

  /*──── delete helpers (server-side via files.php) ─────────*/
  const apiDeleteKey = async (key) => {
    await apiPost({ action: "delete", key });
  };

  // Recursively collect all object keys under a plain prefix like "Leases/foo/bar/"
  const gatherKeysRecursive = async (plainPrefix) => {
    const slug = companySlug();
    const toPlain = (fullKey) =>
      fullKey.startsWith(`${slug}/`) ? fullKey.slice(slug.length + 1) : fullKey;

    const ensureSlash = (s) => (s.endsWith("/") ? s : `${s}/`);

    const results = [];
    const stack = [ensureSlash(plainPrefix)];

    while (stack.length) {
      const cur = stack.pop();
      const entries = await listFiles(cur); // one level

      for (const o of entries) {
        if (!o?.Key) continue;
        if (o.type === "file") {
          results.push(o.Key);
        } else if (o.type === "folder") {
          const nextPlain = ensureSlash(toPlain(o.Key));
          stack.push(nextPlain);
        } else {
          // Heuristic: if it’s not explicitly a file row and has deeper path, keep walking
          const k = o.Key;
          const rel = k.startsWith(`${slug}/`) ? k.slice(slug.length + 1) : k;
          if (rel.startsWith(cur) && rel !== cur) {
            const nextSeg = rel.slice(cur.length).split("/")[0];
            if (nextSeg) stack.push(cur + nextSeg + "/");
          }
        }
      }
    }
    return results;
  };

  const deleteFile = async (key) => {
    if (!window.confirm("Delete this file?")) return;
    setBusy(true);
    try {
      await apiDeleteKey(key);
      refresh();
    } catch (e) {
      console.error("Delete failed:", e);
      alert("Delete failed (see console).");
    } finally {
      setBusy(false);
    }
  };

  const deleteFolder = async (folderName) => {
    if (
      !window.confirm(
        `Delete folder "${folderName}" and ALL its contents? This cannot be undone.`
      )
    )
      return;
    setBusy(true);
    try {
      const plain = ROOT + prefix + folderName + "/";
      const keys = await gatherKeysRecursive(plain);
      await deleteObjects(keys);
      refresh();
    } catch (e) {
      console.error("Folder delete failed:", e);
      alert("Folder delete failed (see console).");
    } finally {
      setBusy(false);
    }
  };

  const deleteObjects = async (keys) => {
    if (!keys.length) return;
    // controlled concurrency
    const MAX_CONCURRENCY = 6;
    let i = 0;
    const errs = [];

    async function worker() {
      while (i < keys.length) {
        const idx = i++;
        const k = keys[idx];
        try {
          await apiDeleteKey(k);
        } catch (e) {
          console.error("Delete key failed:", k, e);
          errs.push(k);
        }
      }
    }

    const workers = [];
    const n = Math.min(MAX_CONCURRENCY, keys.length);
    for (let w = 0; w < n; w++) workers.push(worker());
    await Promise.all(workers);

    if (errs.length) {
      throw new Error(`Failed to delete ${errs.length}/${keys.length} objects`);
    }
  };

  /*──── preview helpers ───────────────────────────────────*/
  const openPrev = (i) => {
    setZoom(1);
    setIdx(i);
  };
  const closePrev = () => setIdx(null);
  const navPrev = (d) =>
    setIdx((i) =>
      i === null ? null : (i + d + fileHits.length) % fileHits.length
    );

  /*──── hotkeys in preview ─────────────────────────────────*/
  useEffect(() => {
    const h = (e) => {
      if (idx === null) return;
      if (e.key === "Escape") closePrev();
      if (e.key === "ArrowRight") navPrev(1);
      if (e.key === "ArrowLeft") navPrev(-1);
      if (["+", "="].includes(e.key)) setZoom((z) => Math.min(z + 0.25, 5));
      if (["-", "_"].includes(e.key)) setZoom((z) => Math.max(z - 0.25, 0.5));
      if (e.key === "0") setZoom(1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [idx, fileHits.length]);

  const pad = "py-1 px-2";
  const label = companyName || companySlug();

  /*──── JSX ───────────────────────────────────────────────*/
  return (
    <Modal open={open} onClose={onClose} theme={theme}>
      {/* header */}
      <header
        className={`px-6 pt-5 pb-3 border-b flex flex-wrap gap-2 items-start justify-between ${
          theme === "dark"
            ? "border-gray-700 bg-gray-800 text-gray-100"
            : "border-gray-200 bg-white text-gray-800"
        }`}
      >
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FontAwesomeIcon icon={faFolderOpen} />
            Leases – {label}
          </h3>
        </div>

        {/* action buttons */}
        <div className="flex flex-wrap gap-2 items-center">
          <input
            ref={fileRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              doUpload(e.target.files);
              e.target.value = "";
            }}
          />
          <button
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5"
            disabled={busy}
          >
            <FontAwesomeIcon icon={faUpload} /> Upload
          </button>
          <button
            onClick={async () => {
              const name = prompt("Folder name:");
              if (!name) return;
              const blob = new Blob([]); // placeholder .keep
              // @ts-ignore – attach name for uploadFile()
              blob.name = ".keep";
              await uploadFile(blob, prefix + name);
              refresh();
            }}
            className="flex items-center gap-1 rounded bg-gray-300 hover:bg-gray-400 text-gray-800 text-xs px-3 py-1.5"
          >
            <FontAwesomeIcon icon={faPlus} /> Folder
          </button>
          <button
            onClick={onClose}
            className="ml-2 text-gray-500 hover:text-gray-700 text-xl px-2"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </header>

      {/* breadcrumb */}
      <nav className="px-6 mt-2 text-xs flex flex-wrap gap-1 text-blue-700 dark:text-blue-400">
        <span
          className="cursor-pointer hover:underline"
          onClick={() => setPrefix("")}
        >
          {label}
        </span>
        {prefix
          .slice(0, -1)
          .split("/")
          .filter(Boolean)
          .map((seg, i, arr) => {
            const up = arr.slice(0, i + 1).join("/") + "/";
            return (
              <React.Fragment key={up}>
                <span>/</span>
                <span
                  className="cursor-pointer hover:underline"
                  onClick={() => setPrefix(up)}
                >
                  {seg}
                </span>
              </React.Fragment>
            );
          })}
      </nav>

      {/* search / sort / view */}
      <div className="px-6 mt-3 flex flex-wrap gap-2 items-center">
        <label className="relative flex-1 min-w-[180px]">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search…"
            className={`w-full pl-7 pr-3 py-1.5 rounded border text-sm ${
              theme === "dark"
                ? "bg-gray-800 border-gray-700 text-gray-200 placeholder-gray-400"
                : "bg-white border-gray-300 text-gray-800 placeholder-gray-500"
            }`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <FontAwesomeIcon icon={faSortAmountDown} className="text-sm" />
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className={`rounded py-1 px-2 text-sm ${
            theme === "dark"
              ? "bg-gray-800 border border-gray-700 text-gray-200"
              : "bg-white border border-gray-300 text-gray-700"
          }`}
        >
          <option value="name">Name</option>
          <option value="date">Date</option>
          <option value="size">Size</option>
        </select>
        <button
          className={`px-2 rounded border text-sm ${
            theme === "dark"
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-300"
          }`}
          onClick={() => setAsc((b) => !b)}
        >
          {asc ? "↑" : "↓"}
        </button>
        <button
          className={`px-2 rounded border text-sm ${
            view === "list"
              ? "bg-blue-600 text-white"
              : theme === "dark"
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-300"
          }`}
          onClick={() => {
            const v = view === "list" ? "grid" : "list";
            setView(v);
            localStorage.setItem(LS.VIEW, v);
          }}
        >
          <FontAwesomeIcon icon={view === "list" ? faTh : faBars} />
        </button>
      </div>

      {/* body */}
      <section
        className="px-6 pb-6 overflow-y-auto"
        style={{ height: "calc(75vh - 165px)" }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          doUpload(e.dataTransfer.files);
        }}
      >
        {prefix && (
          <button
            className="mt-3 mb-2 flex items-center gap-1 text-blue-600 dark:text-blue-400 text-sm hover:underline"
            onClick={() => setPrefix(prefix.replace(/[^/]+\/$/, ""))}
          >
            <FontAwesomeIcon icon={faChevronLeft} /> Back
          </button>
        )}

        {busy && (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin h-10 w-10 rounded-full border-t-2 border-b-2 border-blue-500" />
          </div>
        )}

        {!busy && dirHits.length === 0 && fileHits.length === 0 && (
          <p
            className={`mt-10 text-center ${
              theme === "dark" ? "text-gray-500" : "text-gray-600"
            }`}
          >
            Empty folder.
          </p>
        )}

        {/* folders */}
        {!busy && dirHits.length > 0 && (
          <>
            <h4 className="mt-4 mb-1 font-semibold text-sm">Folders</h4>
            <div
              className={
                view === "grid"
                  ? "grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4"
                  : "flex flex-col divide-y divide-gray-200 dark:divide-gray-700"
              }
            >
              {dirHits.map((d) =>
                view === "grid" ? (
                  <div
                    key={d}
                    className={`group relative flex flex-col items-center justify-center p-3 rounded border text-xs cursor-pointer ${
                      theme === "dark"
                        ? "border-gray-700 bg-gray-800 hover:bg-gray-700"
                        : "border-gray-200 bg-white hover:bg-blue-50"
                    }`}
                    onClick={() => setPrefix(prefix + d + "/")}
                  >
                    <FontAwesomeIcon
                      icon={faFolder}
                      className="text-yellow-500 h-6 w-6 mb-1"
                    />
                    <span className="truncate">{d}</span>

                    {/* delete (grid) */}
                    <button
                      className="absolute top-1 right-1 text-xs text-red-600 hover:text-red-800 opacity-0 group-hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteFolder(d);
                      }}
                      title="Delete folder"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                ) : (
                  <div
                    key={d}
                    className={`flex items-center gap-2 ${pad} cursor-pointer ${
                      theme === "dark"
                        ? "hover:bg-gray-700"
                        : "hover:bg-blue-50"
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={faFolder}
                      className="text-yellow-500"
                      onClick={() => setPrefix(prefix + d + "/")}
                    />
                    <span
                      className="flex-1 truncate"
                      onClick={() => setPrefix(prefix + d + "/")}
                    >
                      {d}
                    </span>
                    <button
                      className="text-red-600 hover:text-red-800"
                      onClick={() => deleteFolder(d)}
                      title="Delete folder"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                )
              )}
            </div>
          </>
        )}

        {/* files */}
        {!busy && fileHits.length > 0 && (
          <>
            <h4 className="mt-6 mb-1 font-semibold text-sm">
              Files ({fileHits.length})
            </h4>
            {view === "grid" ? (
              <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
                {fileHits.map((f, i) => {
                  const box =
                    "relative w-full h-28 flex flex-col items-center justify-center rounded border cursor-pointer text-xs";
                  if (f.kind === "img")
                    return (
                      <div
                        key={f.key}
                        className={`${box} overflow-hidden ${
                          theme === "dark"
                            ? "border-gray-700"
                            : "border-gray-200"
                        }`}
                      >
                        <img
                          src={f.url}
                          alt={f.rel}
                          loading="lazy"
                          className="object-cover w-full h-full"
                          onClick={() => openPrev(i)}
                        />
                        <button
                          className="absolute top-1 right-1 text-xs text-red-600 hover:text-red-800 bg-white/80 rounded-full p-0.5"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteFile(f.key);
                          }}
                          title="Delete file"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    );
                  const color =
                    f.kind === "pdf"
                      ? "text-red-600"
                      : f.kind === "xls"
                      ? "text-green-600"
                      : "text-blue-600";
                  return (
                    <div
                      key={f.key}
                      className={`${box} ${
                        theme === "dark"
                          ? "border-gray-700 bg-gray-800 hover:bg-gray-700"
                          : "border-gray-200 bg-white hover:bg-gray-50"
                      }`}
                      onClick={() => openPrev(i)}
                    >
                      <FontAwesomeIcon
                        icon={iconOf(f.kind)}
                        className={`${color} h-6 w-6 mb-1`}
                      />
                      <span className="truncate px-1">{f.rel}</span>

                      <button
                        className="absolute top-1 right-1 text-xs text-red-600 hover:text-red-800"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteFile(f.key);
                        }}
                        title="Delete file"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <table className="w-full text-xs border rounded overflow-hidden">
                <thead
                  className={
                    theme === "dark"
                      ? "bg-gray-800 text-gray-200"
                      : "bg-gray-100 text-gray-700"
                  }
                >
                  <tr>
                    <th className={pad + " w-6"}></th>
                    <th className={pad + " text-left"}>Name</th>
                    <th className={pad + " text-left"}>Date</th>
                    <th className={pad + " text-left"}>Size</th>
                    <th className={pad}></th>
                  </tr>
                </thead>
                <tbody>
                  {fileHits.map((f, i) => (
                    <tr
                      key={f.key}
                      className={`border-b ${
                        theme === "dark"
                          ? "border-gray-700 hover:bg-gray-800"
                          : "border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      <td className={`${pad} text-center`}>
                        <FontAwesomeIcon icon={iconOf(f.kind || "pdf")} />
                      </td>
                      <td
                        className={`${pad} truncate cursor-pointer`}
                        onClick={() => openPrev(i)}
                      >
                        {f.rel}
                      </td>
                      <td className={pad}>
                        {f.date ? f.date.toLocaleDateString() : "—"}
                      </td>
                      <td className={pad}>{fmtSz(f.size || 0)}</td>
                      <td className={pad}>
                        <button
                          className="text-red-600 hover:text-red-800"
                          onClick={() => deleteFile(f.key)}
                          title="Delete file"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </section>

      {/* preview */}
      {idx !== null && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80"
          onClick={(e) => e.target === e.currentTarget && closePrev()}
        >
          <button
            className="absolute left-4 md:left-8 text-white/80 hover:text-white text-4xl"
            onClick={() => navPrev(-1)}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <button
            className="absolute right-4 md:right-8 text-white/80 hover:text-white text-4xl"
            onClick={() => navPrev(1)}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>

          <div className="relative max-w-[95vw] max-h-[85vh] p-2">
            <button
              className="absolute -top-4 -right-4 bg-black/70 text-white p-2 rounded-full"
              onClick={closePrev}
            >
              <FontAwesomeIcon icon={faTimes} size="sm" />
            </button>

            <div className="absolute top-4 left-1/2 -translate-x-1/2 flex gap-4 text-white">
              <button
                className="bg-black/70 p-3 rounded-full"
                onClick={() => setZoom((z) => Math.min(z + 0.25, 5))}
              >
                <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
              </button>
              <button
                className="bg-black/70 p-3 rounded-full"
                onClick={() => setZoom((z) => Math.max(z - 0.25, 0.5))}
              >
                <FontAwesomeIcon icon={faMagnifyingGlassMinus} />
              </button>
              <button
                className="bg-black/70 p-3 rounded-full"
                onClick={() => setZoom(1)}
              >
                <FontAwesomeIcon icon={faUndo} />
              </button>
            </div>

            {(() => {
              const f = fileHits[idx];
              const box = {
                maxWidth: "95vw",
                maxHeight: "85vh",
                width: "95vw",
                height: "85vh",
              };
              if (f.kind === "img")
                return (
                  <img
                    src={f.url}
                    alt={f.rel}
                    style={{ ...box, width: `${zoom * 100}%` }}
                    className="object-contain"
                  />
                );
              if (f.kind === "pdf") {
                const z = Math.round(zoom * 100);
                return (
                  <object
                    key={z}
                    data={`${f.url}#view=FitH&zoom=${z}`}
                    type="application/pdf"
                    style={{
                      ...box,
                      transform: `scale(${zoom})`,
                      transformOrigin: "top left",
                    }}
                  />
                );
              }
              if (f.kind === "doc" || f.kind === "xls")
                return (
                  <iframe
                    key={Math.round(zoom * 100)}
                    src={officeUrl(f.url)}
                    title={f.rel}
                    style={{
                      ...box,
                      transform: `scale(${zoom})`,
                      transformOrigin: "top left",
                    }}
                  />
                );
              return (
                <a
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white underline text-lg"
                >
                  Open {f.rel}
                </a>
              );
            })()}
          </div>
        </div>
      )}
    </Modal>
  );
}
